import { type ReactElement } from 'react'
import { Controller, type FieldValues } from 'react-hook-form'

import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

import { getFieldErrorMessages } from 'global/helpers/form'
import type { KNFormTextProps } from './types'

const KNFormText = <T extends FieldValues>({
  name,
  placeholder,
  control,
  rules,
  onType,
  label,
  size,
  sx,
  disabled,
  rows,
  type,
  readonly,
  autoFocus,
  startAdornment,
  endAdornment,
  transformValue,
}: KNFormTextProps<T>): ReactElement => {
  const getAdornment = (adornment: ReactElement | string, position: 'start' | 'end'): ReactElement => {
    if (typeof adornment === 'string') {
      return <InputAdornment position={position}>{adornment}</InputAdornment>
    }
    return adornment
  }

  const getStartAdornment = (): ReactElement | null => (startAdornment ? getAdornment(startAdornment, 'start') : null)

  const getEndAdornment = (): ReactElement | null => (endAdornment ? getAdornment(endAdornment, 'end') : null)

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value, ref }, fieldState: { invalid, error }, formState }): ReactElement => (
        <TextField
          fullWidth
          label={label}
          placeholder={placeholder}
          size={size}
          sx={sx}
          disabled={formState.isSubmitting || disabled}
          multiline={Boolean(rows && rows > 1)}
          rows={rows}
          inputRef={ref}
          value={value ?? ''}
          onChange={(event): void => {
            onChange(transformValue ? transformValue(event.target.value) : event.target.value)
            onType?.()
          }}
          error={invalid}
          helperText={getFieldErrorMessages(error)}
          slotProps={{
            input: {
              type: type ?? 'text',
              startAdornment: getStartAdornment(),
              endAdornment: getEndAdornment(),
              disabled: readonly,
            },
          }}
          autoFocus={autoFocus}
        />
      )}
    />
  )
}

export default KNFormText
